"use client";

import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

import { type GlobePalette, shadeGlobe } from "./globe-shading";

/** Longitude facing the viewer on first paint: the Americas. */
const START_LON = -100;
/** Degrees of rotation per second at rest. */
const SPEED = 6;
const REVEAL_SECONDS = 1.1;

/** Lattice cells across the canvas, so the grain holds at any size. */
const CELLS_ACROSS = 70;
/** Dot radius per shading level (`GLYPHS` index), as a share of the cell. */
const DOT_RADIUS = [0.12, 0.26, 0.36, 0.44];

// White dots for the blue cards. Land opacity follows the lighting; experts
// are the only solid dots, so they read as lights on the disc.
const WHITE_ON_BLUE: GlobePalette = {
  ocean: "rgba(255, 255, 255, 0.24)",
  land: alphaRamp([255, 255, 255], 0.3, 1, 16),
  experts: alphaRamp([255, 255, 240], 0.75, 1, 16),
};

function alphaRamp(
  rgb: [number, number, number],
  from: number,
  to: number,
  steps: number,
): string[] {
  return Array.from({ length: steps }, (_, i) => {
    const alpha = from + ((to - from) * i) / (steps - 1);
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha.toFixed(3)})`;
  });
}

/**
 * The About globe drawn as a halftone dot field. Shading comes from
 * `shadeGlobe`, the same source as the About share image; each cell becomes a
 * dot sized by its shading level.
 */
export function HalftoneGlobe({ className }: { className?: string }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!wrap || !canvas || !ctx) {
      return;
    }

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    let size = 0;
    let cell = 8;

    let lon0 = START_LON;
    let elapsed = 0;
    let reveal = reducedMotion.matches ? 1 : 0;

    let frame = 0;
    let last = 0;
    let running = false;
    let onScreen = true;

    function layout() {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      size = Math.max(1, Math.round(wrap!.getBoundingClientRect().width));
      canvas!.width = Math.round(size * dpr);
      canvas!.height = Math.round(size * dpr);
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      cell = size / CELLS_ACROSS;
      draw();
    }

    function draw() {
      ctx!.clearRect(0, 0, size, size);

      // One path per colour keeps a frame to a few dozen fills.
      const paths = new Map<string, Path2D>();
      shadeGlobe(
        {
          size,
          cellW: cell,
          cellH: cell,
          lon0,
          elapsed,
          reveal,
          palette: WHITE_ON_BLUE,
        },
        (glyph, color, px, py) => {
          let path = paths.get(color);
          if (!path) {
            path = new Path2D();
            paths.set(color, path);
          }
          const r = cell * (DOT_RADIUS[glyph] ?? 0);
          path.moveTo(px + r, py);
          path.arc(px, py, r, 0, Math.PI * 2);
        },
      );
      for (const [color, path] of paths) {
        ctx!.fillStyle = color;
        ctx!.fill(path);
      }
    }

    function tick(now: number) {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      elapsed += dt;
      reveal = Math.min(1, reveal + dt / REVEAL_SECONDS);
      lon0 += dt * SPEED;
      draw();
      frame = requestAnimationFrame(tick);
    }

    function sync() {
      const shouldRun =
        onScreen &&
        document.visibilityState === "visible" &&
        !reducedMotion.matches;
      if (shouldRun && !running) {
        running = true;
        last = performance.now();
        frame = requestAnimationFrame(tick);
      } else if (!shouldRun && running) {
        running = false;
        cancelAnimationFrame(frame);
      }
      if (reducedMotion.matches) {
        reveal = 1;
        draw();
      }
    }

    const resize = new ResizeObserver(layout);
    resize.observe(wrap);
    const intersect = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry?.isIntersecting ?? true;
        sync();
      },
      { rootMargin: "64px" },
    );
    intersect.observe(wrap);
    document.addEventListener("visibilitychange", sync);
    reducedMotion.addEventListener("change", sync);

    layout();
    sync();

    return () => {
      running = false;
      cancelAnimationFrame(frame);
      resize.disconnect();
      intersect.disconnect();
      document.removeEventListener("visibilitychange", sync);
      reducedMotion.removeEventListener("change", sync);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className={cn("relative aspect-square w-full select-none", className)}
    >
      <canvas ref={canvasRef} className="block size-full" />
    </div>
  );
}
